import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Typography, ButtonBase, Menu, MenuItem, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

/* ─── Types ──────────────────────────────────────────────────────────────────── */

export interface CubeFace {
  key: string;
  label: string;
  shortLabel?: string;
  content: React.ReactNode;
  /**
   * Explicit navigation edges, for suites whose faces form a branching path rather than
   * a ring. `undefined` keeps the wrap-around behaviour; `null` means the arrow has
   * nowhere to go and is hidden. Either side may list several keys, which turns that
   * arrow into a choice — a face can be arrived at from a fork as well as lead to one.
   */
  prev?: string | string[] | null;
  next?: string | string[] | null;
}

/** Where the arrows on either side of the strip point, for one face. */
export interface CubeEdges {
  prev: CubeFace[];
  next: CubeFace[];
}

/**
 * Resolve the navigation arrows for `activeKey`.
 *
 * A face that declares either edge opts the whole strip out of the wrap-around ring, so
 * a suite can describe a branching path instead. Targets naming a face that isn't in the
 * list are dropped rather than erroring — access gating removes whole faces, and a route
 * the user cannot reach should simply not be offered.
 */
export function resolveCubeEdges(faces: CubeFace[], activeKey: string): CubeEdges {
  const idx = faces.findIndex((f) => f.key === activeKey);
  if (idx === -1 || faces.length === 0) return { prev: [], next: [] };

  const active = faces[idx];
  const usesGraph = active.prev !== undefined || active.next !== undefined;

  if (!usesGraph) {
    return {
      prev: [faces[(idx - 1 + faces.length) % faces.length]],
      next: [faces[(idx + 1) % faces.length]],
    };
  }

  const byKey = (key: string) => faces.find((f) => f.key === key);
  const resolve = (declared: string | string[] | null | undefined) =>
    (declared ? (Array.isArray(declared) ? declared : [declared]) : [])
      .map(byKey)
      .filter((f): f is CubeFace => Boolean(f));

  return { prev: resolve(active.prev), next: resolve(active.next) };
}

interface CubeTransitionProps {
  faces: CubeFace[];
  activeKey: string;
  onNavigate: (key: string) => void;
}

/* ─── Constants ──────────────────────────────────────────────────────────────── */

const ANIM_MS = 650;
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

/* ─── Keyframes (injected once) ──────────────────────────────────────────────── */

const CUBE_STYLE_ID = 'cube-transition-keyframes';

function ensureKeyframes() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(CUBE_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = CUBE_STYLE_ID;
  style.textContent = `
    @keyframes cubeOutRight {
      0%   { transform: rotateY(0deg);   opacity: 1; }
      30%  { opacity: 1; }
      100% { transform: rotateY(-90deg); opacity: 0.35; }
    }
    @keyframes cubeInRight {
      0%   { transform: rotateY(90deg);  opacity: 0.35; }
      70%  { opacity: 1; }
      100% { transform: rotateY(0deg);   opacity: 1; }
    }
    @keyframes cubeOutLeft {
      0%   { transform: rotateY(0deg);   opacity: 1; }
      30%  { opacity: 1; }
      100% { transform: rotateY(90deg);  opacity: 0.35; }
    }
    @keyframes cubeInLeft {
      0%   { transform: rotateY(-90deg); opacity: 0.35; }
      70%  { opacity: 1; }
      100% { transform: rotateY(0deg);   opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

/* ─── Component ──────────────────────────────────────────────────────────────── */

export default function CubeTransition({ faces, activeKey, onNavigate }: CubeTransitionProps) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  // Track which faces have been visited so we lazy-mount them (preserving state once mounted)
  const [visitedKeys, setVisitedKeys] = useState<Set<string>>(() => new Set([activeKey]));

  // The key that is currently "settled" on screen (updates after animation ends)
  const [settledKey, setSettledKey] = useState(activeKey);

  const [anim, setAnim] = useState<{
    active: boolean;
    direction: 'left' | 'right';
    fromKey: string;
    toKey: string;
  }>({ active: false, direction: 'right', fromKey: activeKey, toKey: activeKey });

  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Inject keyframes stylesheet once
  useEffect(() => { ensureKeyframes(); }, []);

  const indexOf = useCallback(
    (key: string) => faces.findIndex((f) => f.key === key),
    [faces],
  );

  // Mark activeKey as visited
  useEffect(() => {
    setVisitedKeys((prev) => {
      if (prev.has(activeKey)) return prev;
      const next = new Set(prev);
      next.add(activeKey);
      return next;
    });
  }, [activeKey]);

  // Trigger animation when activeKey changes
  useEffect(() => {
    if (activeKey === settledKey) return;

    const fromIdx = indexOf(settledKey);
    const toIdx = indexOf(activeKey);
    if (toIdx === -1) return;

    const n = faces.length;
    const fwd = (toIdx - fromIdx + n) % n;
    const direction: 'left' | 'right' =
      pendingDirectionRef.current ?? (fwd <= n / 2 ? 'right' : 'left');
    pendingDirectionRef.current = null;

    setAnim({ active: true, direction, fromKey: settledKey, toKey: activeKey });

    timerRef.current = setTimeout(() => {
      setSettledKey(activeKey);
      setAnim((prev) => ({ ...prev, active: false }));
    }, ANIM_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [activeKey, settledKey, faces.length, indexOf]);

  // Compute prev/next for navigation buttons (based on target key)
  const effectiveKey = anim.active ? anim.toKey : settledKey;
  const { prev: prevFaces, next: nextFaces } = useMemo(
    () => resolveCubeEdges(faces, effectiveKey),
    [faces, effectiveKey],
  );

  // One menu serves both forks; it remembers which side opened it so it drops below the
  // arrows it belongs to.
  const [branchMenu, setBranchMenu] =
    useState<{ anchor: HTMLElement; faces: CubeFace[]; align: 'left' | 'right' } | null>(null);

  // Which way the cube should spin. The index heuristic below reads the face order, which
  // is meaningless once the suite loops (the Maximizer sits at index 0 yet is reached by
  // going *back* from the last face), so an arrow the user actually clicked states its
  // own direction and wins.
  const pendingDirectionRef = useRef<'left' | 'right' | null>(null);

  const goTo = useCallback((key: string, direction?: 'left' | 'right') => {
    if (anim.active) return;
    setBranchMenu(null);
    pendingDirectionRef.current = direction ?? null;
    onNavigate(key);
  }, [anim.active, onNavigate]);

  // Determine the label to show (short on mobile)
  const labelFor = useCallback(
    (face: CubeFace) => (isSmall && face.shortLabel ? face.shortLabel : face.label),
    [isSmall],
  );

  /* ─── Navigation strip ─────────────────────────────────────────────── */

  // Both ends of the strip render the same way: a single destination as one button,
  // several as "A or B", collapsing into a menu on a narrow screen so the strip never
  // overflows. Either end can be a fork — Buying splits forward into Selling/Decoupling,
  // and the Maximizer is reached back from whichever of them the agent can use.
  const renderEdge = useCallback(
    (edgeFaces: CubeFace[], direction: 'back' | 'forward') => {
      if (edgeFaces.length === 0) return <Box sx={{ width: { xs: 40, sm: 90 } }} />;

      const Arrow = direction === 'back' ? ArrowBackIosNewIcon : ArrowForwardIosIcon;
      const spin: 'left' | 'right' = direction === 'back' ? 'left' : 'right';
      const nudge = direction === 'back' ? -1 : 1;

      const buttonSx = (compact: boolean) => ({
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 0.75 : { xs: 0.5, sm: 1 },
        px: compact ? 1.5 : { xs: 1, sm: 2 },
        py: 1,
        borderRadius: 2,
        color: 'var(--color-text-strong)',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: 'rgba(217,119,6,0.12)',
          transform: `translateX(${2 * nudge}px)`,
          '& .nav-arrow': { color: '#D97706' },
        },
        '&:active': { transform: `translateX(${4 * nudge}px) scale(0.98)` },
      });

      // The arrow always points outward, so it leads on the left and trails on the right.
      const inner = (face: CubeFace | null, compact: boolean) => {
        const arrow = (
          <Arrow
            className="nav-arrow"
            sx={{
              fontSize: compact ? 18 : { xs: 14, sm: 18 },
              color: '#D97706',
              transition: 'color 0.2s',
            }}
          />
        );
        const text = (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: compact ? '0.875rem' : { xs: '0.75rem', sm: '0.875rem' },
              whiteSpace: 'nowrap',
            }}
          >
            {face ? labelFor(face) : direction === 'back' ? 'Back' : 'Next'}
          </Typography>
        );
        return direction === 'back'
          ? <>{arrow}{text}</>
          : <>{text}{arrow}</>;
      };

      if (edgeFaces.length === 1 || isSmall) {
        return (
          <ButtonBase
            onClick={(e) => {
              if (edgeFaces.length === 1) goTo(edgeFaces[0].key, spin);
              else {
                setBranchMenu({
                  anchor: e.currentTarget,
                  faces: edgeFaces,
                  align: direction === 'back' ? 'left' : 'right',
                });
              }
            }}
            disabled={anim.active}
            sx={buttonSx(false)}
          >
            {inner(edgeFaces.length === 1 ? edgeFaces[0] : null, false)}
          </ButtonBase>
        );
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {edgeFaces.map((face, idx) => (
            <React.Fragment key={face.key}>
              {idx > 0 && (
                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                  or
                </Typography>
              )}
              <ButtonBase
                onClick={() => goTo(face.key, spin)}
                disabled={anim.active}
                sx={buttonSx(true)}
              >
                {inner(face, true)}
              </ButtonBase>
            </React.Fragment>
          ))}
        </Box>
      );
    },
    [anim.active, goTo, isSmall, labelFor],
  );

  const navStrip = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2.5,
          mx: { xs: -0.5, sm: 0 },
          borderRadius: 2.5,
          overflow: 'hidden',
          background:
            'linear-gradient(135deg, rgba(217,119,6,0.08) 0%, rgba(217,119,6,0.03) 100%)',
          border: '1px solid rgba(217,119,6,0.18)',
          backdropFilter: 'blur(8px)',
          px: { xs: 0.5, sm: 1.5 },
          py: { xs: 0.75, sm: 1 },
        }}
      >
        {renderEdge(prevFaces, 'back')}

        {/* Centre dot indicators */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {faces.map((face) => {
            const isActive = face.key === effectiveKey;
            return (
              <ButtonBase
                key={face.key}
                onClick={() => {
                  if (!anim.active && face.key !== effectiveKey) onNavigate(face.key);
                }}
                sx={{
                  width: isActive ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: isActive ? '#D97706' : 'rgba(217,119,6,0.25)',
                  transition: 'all 0.35s ease',
                  cursor: face.key === effectiveKey ? 'default' : 'pointer',
                  '&:hover': {
                    bgcolor: isActive ? '#D97706' : 'rgba(217,119,6,0.45)',
                  },
                }}
                aria-label={face.label}
              />
            );
          })}
        </Box>

        {renderEdge(nextFaces, 'forward')}

        <Menu
          anchorEl={branchMenu?.anchor ?? null}
          open={!!branchMenu}
          onClose={() => setBranchMenu(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: branchMenu?.align ?? 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: branchMenu?.align ?? 'right' }}
        >
          {(branchMenu?.faces ?? []).map((face) => (
            <MenuItem
              key={face.key}
              onClick={() => goTo(face.key, branchMenu?.align === 'left' ? 'left' : 'right')}
            >
              {face.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    ),
    [
      anim.active,
      branchMenu,
      effectiveKey,
      faces,
      goTo,
      nextFaces,
      onNavigate,
      prevFaces,
      renderEdge,
    ],
  );

  /* ─── Render ───────────────────────────────────────────────────────── */

  return (
    <Box sx={{ width: '100%' }}>
      {navStrip}

      {/* 3D cube viewport */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          perspective: '1200px',
          minHeight: 200,
        }}
      >
        {faces.map((face) => {
          const isCurrent = face.key === settledKey && !anim.active;
          const isAnimOut = anim.active && face.key === anim.fromKey;
          const isAnimIn = anim.active && face.key === anim.toKey;
          const isVisible = isCurrent || isAnimOut || isAnimIn;

          // Determine animation & transform-origin
          let animation = 'none';
          let transformOrigin = 'center';

          if (isAnimOut) {
            if (anim.direction === 'right') {
              animation = `cubeOutRight ${ANIM_MS}ms ${EASING} forwards`;
              transformOrigin = 'right center';
            } else {
              animation = `cubeOutLeft ${ANIM_MS}ms ${EASING} forwards`;
              transformOrigin = 'left center';
            }
          } else if (isAnimIn) {
            if (anim.direction === 'right') {
              animation = `cubeInRight ${ANIM_MS}ms ${EASING} forwards`;
              transformOrigin = 'left center';
            } else {
              animation = `cubeInLeft ${ANIM_MS}ms ${EASING} forwards`;
              transformOrigin = 'right center';
            }
          }

          return (
            <Box
              key={face.key}
              sx={{
                // Active idle face flows normally; others are absolutely overlaid
                ...(isCurrent
                  ? { position: 'relative' }
                  : {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      // Idle hidden faces are clipped to zero height: absolutely-positioned
                      // content still counts toward the scroll container's overflow, so a
                      // tall inactive calculator would otherwise stretch the page with
                      // phantom whitespace below a short active face (e.g. 2-Scroll mode).
                      // Kept mounted (not display:none) so face state survives navigation.
                      ...(isVisible ? {} : { height: 0, overflow: 'hidden' }),
                    }),
                visibility: isVisible ? 'visible' : 'hidden',
                pointerEvents: isCurrent ? 'auto' : 'none',
                animation,
                transformOrigin,
                backfaceVisibility: 'hidden',
                zIndex: isAnimIn ? 2 : isAnimOut ? 1 : 0,
                willChange: isVisible ? 'transform, opacity' : 'auto',
              }}
            >
              {visitedKeys.has(face.key) ? face.content : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
